import { z } from 'zod';
import { AppModeSchema, RoleSchema } from './enums';
import { PermissionSchema } from './rbac';

/** §15, §3 ТЗ. Контракты авторизации и стартовой загрузки SPA. */

export const LoginRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  // §18: минимальная длина пароля — 12 символов
  newPassword: z.string().min(12).max(200),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

/** Объект, доступный текущему пользователю. Сервер отдаёт только разрешённые (§3). */
export const AllowedProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  timezone: z.string(),
});
export type AllowedProject = z.infer<typeof AllowedProjectSchema>;

/**
 * GET /api/session/bootstrap — единственный источник правды для фронта
 * о режиме, правах и доступных объектах. Догадки по URL запрещены.
 */
export const SessionBootstrapSchema = z.object({
  authenticated: z.boolean(),
  mode: AppModeSchema,
  /** Доступна ли интеграция с Bitrix24 прямо сейчас (E10: circuit breaker). */
  b24Available: z.boolean(),
  /**
   * §11.2: адрес портала Bitrix24 — для ссылки «номер задачи → задача».
   * `null` — интеграции нет, номер задачи остаётся текстом. Адрес называет
   * сервер: догадки по URL запрещены (см. выше).
   */
  b24PortalUrl: z.string().url().nullable(),
  employee: z
    .object({
      id: z.string().uuid(),
      fullName: z.string(),
      email: z.string().email(),
      role: RoleSchema,
      mustChangePassword: z.boolean(),
      source: z.enum(['VIBECODE', 'LOCAL']),
    })
    .nullable(),
  /** null означает «все объекты» (SYS_ADMIN, MANAGER_HEAD, ANALYST). */
  allowedProjects: z.array(AllowedProjectSchema).nullable(),
  /**
   * §3: готовый список прав роли. SPA не выводит права из роли сама —
   * иначе матрица оказалась бы в двух местах и разошлась бы.
   * Неавторизованному приходит пустой список.
   */
  permissions: z.array(PermissionSchema),
  defaultTimezone: z.string(),
});
export type SessionBootstrap = z.infer<typeof SessionBootstrapSchema>;

/** §15.1: обмен placement-контекста VibeCode на серверную сессию. */
export const VibecodePlacementRequestSchema = z.object({
  placementCode: z.string().min(1).max(4096),
});
export type VibecodePlacementRequest = z.infer<typeof VibecodePlacementRequestSchema>;

/**
 * §15.1: почему вход из портала не состоялся. Повод уезжает в адрес формы
 * входа (`/login?placement=…`), а русский текст к нему пишет SPA.
 *
 * Список закрытый: наружу не выходит ни сообщение шлюза, ни код его ответа —
 * только то, что человеку нужно прочитать. Кодируют повод двое — сервер на
 * редиректе встройки и SPA при отказе JSON-обмена, — поэтому таблица живёт
 * здесь: вторая копия однажды разошлась бы текстами.
 */
export const PlacementFailureSchema = z.enum([
  'not-registered',
  'rejected',
  'unavailable',
  'too-many',
]);
export type PlacementFailure = z.infer<typeof PlacementFailureSchema>;

/** Код ошибки входа через портал → повод для формы. Незнакомый код — «недоступен». */
export function placementFailureOfCode(code: unknown): PlacementFailure {
  switch (code) {
    case 'PORTAL_USER_NOT_REGISTERED':
      return 'not-registered';
    case 'PLACEMENT_REJECTED':
      return 'rejected';
    case 'TOO_MANY_ATTEMPTS':
      return 'too-many';
    default:
      return 'unavailable';
  }
}
